<template>
  <div id="tdWebchat" data-testid="chatDiv" />
</template>

<script lang="ts" setup>
import { assertIsDefined } from '~/utils/assert'
import { useLocaleStore } from '~/stores/locale'
import type { ValueByCulture } from '~/types/root'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'
import { UserRole } from '~/types/account'

// Talkdesk v2 web chat, ported from nuxt2 CustomerChat.vue. This component was
// a copy of the legacy v1 widget nuxt2 deleted in PR 19663: it passed flowId,
// shipped its own styling, and never sent context, so the support agent could
// not see who was chatting. The widget's look now comes from the Talkdesk
// touchpoint, as in nuxt2. AB#64456

const $config = useTypedRuntimeConfig()

const { user } = useSAuth()

const init = (id: string, region: string) => {
  assertIsDefined(window.TalkdeskChatSDK, 'TalkdeskChatSDK should be defined in app chat')
  const webChat = window.TalkdeskChatSDK('tdWebchat', { touchpointId: id, accountId: '', region, shadowDOM: false })
  webChat.init()
  // Tell the agent who is chatting. Read when the conversation starts, so it
  // reflects the user signed in at that moment. Chat renders only for
  // customers and demonstrators, so isDemo is the demonstrator role (nuxt2
  // rootStore.isDemo also counted an agent in demo mode, who never sees chat).
  webChat.onConversationStart = () => {
    webChat.setContextParam({
      firstName: user.value?.firstName,
      lastName: user.value?.lastName,
      email: user.value?.email,
      isDemo: user.value?.role === UserRole.Demonstrator,
      demoId: user.value?.demonstrator?.demoId
    })
  }
}

const scriptId = 'tdwebchatscript'
const chatUrl = $config.public.CHAT_URL
const flowId = (Array.isArray($config.public.CHAT_FLOW_ID) ? $config.public.CHAT_FLOW_ID : []) as ValueByCulture[]
const region = $config.public.CHAT_REGION
assertIsDefined(chatUrl, 'CHAT_URL config value should be defined')
assertIsDefined(flowId, 'CHAT_FLOW_ID config value should be defined')
assertIsDefined(region, 'CHAT_REGION config value should be defined')

const localeStore = useLocaleStore()
// Degrade gracefully when chat isn't configured for this culture. AppChat renders for authed
// users on every page, so a missing/malformed CHAT_FLOW_ID must yield "no widget", never a 500.
const id = flowId.find(v => v.name === localeStore.culture)?.value

if (import.meta.client && id && !document.querySelector('.digital-webchat-wrapper')) {
  if (window.TalkdeskChatSDK) {
    init(id, region)
  } else {
    useHead({
      script: [{
        key: scriptId,
        src: chatUrl,
        defer: true,
        onload: () => {
          setTimeout(() => {
            init(id, region)
          }, 1500)
        }
      }]
    })
  }
}

onMounted(() => {
  if (id && window.TalkdeskChatSDK) {
    init(id, region)
  }
})

onBeforeUnmount(() => {
  document.querySelector('.digital-webchat-wrapper')?.remove()
})

</script>
<style lang='scss' scoped>
// nuxt2 CustomerChat: the v1 #botonic-webchat selector this replaced matches
// nothing in the v2 widget.
#tdWebchat {
  z-index: 2147483638;
}
</style>
